import React, { useCallback, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { DndContext, DragOverlay, closestCenter } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '@/lib/utils';
import { clock } from '@/lib/studio/format';
import type { SceneState } from '@/lib/studio/studioState';
import type { VideoManifest } from '@/types/videoManifest';
import { playheadAt, timeAt, timelineBlocks, type TimelineBlock } from '@/lib/studioB/timeline';
import { FilmstripSlideGap } from './FilmstripSlideGap';
import { IconTipB } from '@/components/studio/IconTipB';
import type { StudioFilmstripProps } from './StudioFilmstrip';
import { SCENE_DRAG_ACCESSIBILITY } from '@/lib/studio/sceneDrag';

export interface StudioTimelineBProps extends Omit<StudioFilmstripProps, 'conversationId' | 'showFilmstrip'> {
  manifest: VideoManifest | null;
  /** The whole video's time, or, when one scene plays on its own, the time in that scene. */
  time: number;
  wholeVideo: boolean;
  sceneDuration: number;
  onSeek: (time: number, slideId: string) => void;
  /** The newest draft of each scene still building, by scene id. */
  drafts?: Record<string, string>;
  /** The studio's scenes and the clock they run on: a building tile shows its time and draft, one not started yet waits. */
  scenes?: SceneState[];
  now?: number;
}

function SortableScene({ id, scene, grow, disabled, children }: { id: string; scene: string; grow: number; disabled: boolean; children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  // The scene itself is the keyboard handle: Enter on the tile button inside it seeks, it does not pick the scene up.
  const ref = useCallback((n: HTMLElement | null) => { setNodeRef(n); setActivatorNodeRef(n); }, [setNodeRef, setActivatorNodeRef]);
  return (
    <div
      ref={ref}
      data-scene={scene}
      {...attributes}
      {...listeners}
      style={{ flexGrow: grow, flexBasis: 0, transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
      className="group/scene relative min-w-14"
    >
      {children}
    </div>
  );
}

function Playhead({ fraction }: { fraction: number }) {
  return (
    <div
      data-playhead
      aria-hidden="true"
      style={{ left: `${Math.round(fraction * 1000) / 10}%` }}
      className="pointer-events-none absolute -bottom-1.5 -top-1.5 z-10 w-0.5 -translate-x-1/2 rounded-full bg-b-ink"
    >
      <span className="absolute -left-1 -top-0.5 h-2.5 w-2.5 rounded-full bg-b-ink" />
    </div>
  );
}

function sceneLabel(n: number, block: TimelineBlock, status: string, draft: boolean) {
  const tail = draft ? 'Draft' : status === 'generating' ? 'Building' : status === 'failed' ? 'Failed' : block.seconds != null ? `${Math.round(block.seconds)} s` : null;
  return [n, block.title, tail].filter((p) => p != null).join(' · ');
}

/** While the build runs: "2 · Building 2:14 · draft 1" with its clock, or "3 · Waiting"; the tooltip names the scene. */
function buildLabel(n: number, block: TimelineBlock, scene: SceneState | undefined, waiting: boolean, now: number) {
  if (scene?.status === 'building' && scene.startedAt != null) {
    const time = clock(now - scene.startedAt);
    const state = [`Building ${time}`, scene.drafts > 0 ? `draft ${scene.drafts}` : null].filter(Boolean).join(' · ');
    return { label: `${n} · ${state}`, clock: time, tooltip: `${n} · ${block.title} · ${state}` };
  }
  if (scene?.status === 'planned' && waiting) return { label: `${n} · Waiting`, tooltip: `${n} · ${block.title} · Waiting` };
  return null;
}

/**
 * Direction B's timeline (beta only): the scenes under the controls, each as wide as it is long, with a playhead.
 * It keeps the filmstrip's work: drag to reorder, insert and transitions between scenes, delete, add, restore.
 */
export function StudioTimelineB({
  slides,
  slideConversations,
  currentSlideId,
  sensors,
  onDragStart,
  onDragEnd,
  activeDragId,
  getStableSlideId,
  onSlideSelect,
  onRequestDelete,
  onOpenSmartAdd,
  getSlideTransition,
  defaultTransition,
  onTransitionUpdate,
  onTransitionClear,
  savingTransitions,
  hasManifest,
  deletedSlides,
  onRestoreDeleted,
  manifest,
  time,
  wholeVideo,
  sceneDuration,
  onSeek,
  drafts = {},
  scenes = [],
  now = 0,
}: StudioTimelineBProps) {
  const [deletedOpen, setDeletedOpen] = useState(false);
  const blocks = timelineBlocks(slides, manifest);
  const head = wholeVideo
    ? playheadAt(blocks, time)
    : currentSlideId ? { id: currentSlideId, fraction: sceneDuration > 0 ? Math.min(1, time / sceneDuration) : 0 } : null;
  const statusOf = (id: string) => slideConversations.find((sc) => sc.outlineSlideId === id)?.status || slides.find((s) => s.id === id)?.status || 'completed';
  const dragged = slides.find((s) => getStableSlideId(s) === activeDragId);
  const buildRunning = scenes.some((x) => x.status === 'building');

  const pick = (block: TimelineBlock, e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const t = wholeVideo ? timeAt(block, rect.width > 0 ? (e.clientX - rect.left) / rect.width : 0) : null;
    if (t == null) onSlideSelect(block.id);
    else onSeek(t, block.id);
  };

  return (
    <div data-timeline="b" className="flex h-[var(--b-timeline)] flex-none items-stretch gap-2 bg-b-chrome px-4 py-1.5 shadow-[inset_0_1px_0_hsl(var(--b-line))]">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={onDragStart} onDragEnd={onDragEnd} accessibility={SCENE_DRAG_ACCESSIBILITY}>
        <SortableContext items={slides.map((s) => getStableSlideId(s))} strategy={horizontalListSortingStrategy}>
          <div className="flex min-w-0 flex-1 items-stretch gap-1 overflow-x-auto py-2">
            {blocks.map((block, i) => {
              const slide = slides[i];
              const next = slides[i + 1];
              const status = statusOf(block.id);
              const building = status === 'generating';
              const current = block.id === currentSlideId;
              const draft = slide.thumbnail ? undefined : drafts[block.id];
              const picture = slide.thumbnail ?? draft;
              const live = buildLabel(i + 1, block, scenes.find((x) => x.id === block.id), buildRunning, now);
              return (
                <SortableScene key={getStableSlideId(slide)} id={getStableSlideId(slide)} scene={block.id} grow={block.weight} disabled={building}>
                  <button
                    type="button"
                    aria-label={`Scene ${i + 1}, ${block.title}`}
                    aria-current={current ? 'true' : undefined}
                    title={live?.tooltip}
                    onClick={(e) => pick(block, e)}
                    className={cn(
                      'b-tile relative block h-full w-full overflow-hidden rounded-[var(--b-radius-control)] bg-b-raised text-left',
                      current ? 'shadow-[0_0_0_2px_hsl(var(--b-ink))]' : 'shadow-[inset_0_0_0_1px_hsl(var(--b-line))]',
                      building && 'motion-safe:animate-pulse',
                    )}
                  >
                    {picture && <img src={picture} alt="" draggable={false} className={cn('absolute inset-0 h-full w-full object-cover', draft && 'opacity-70')} />}
                    <span
                      className={cn(
                        'absolute bottom-1.5 left-1.5 max-w-[calc(100%-12px)] truncate rounded-[5px] px-1.5 py-0.5 text-[11px] font-medium leading-4',
                        picture ? 'bg-black/60 text-white' : 'text-b-t2',
                      )}
                    >
                      {live?.clock ? (
                        <>
                          <span className="b-tile-full">{live.label}</span>
                          <span className="b-tile-clock">{live.clock}</span>
                        </>
                      ) : (
                        live?.label ?? sceneLabel(i + 1, block, status, !!draft)
                      )}
                    </span>
                  </button>
                  {slides.length > 1 && !building && (
                    <IconTipB label="Delete scene" side="top">
                      <button
                        type="button"
                        aria-label={`Delete scene ${i + 1}`}
                        onClick={() => onRequestDelete(block.id, block.title)}
                        className="absolute right-1 top-1 z-10 grid h-5 w-5 place-items-center rounded-full bg-black/60 text-white opacity-0 transition-opacity focus-visible:opacity-100 group-hover/scene:opacity-100"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </IconTipB>
                  )}
                  {head?.id === block.id && <Playhead fraction={head.fraction} />}
                  {next && (
                    <div className="absolute -right-2 bottom-0 top-0 z-20 flex items-center">
                      <FilmstripSlideGap
                        onInsert={() => onOpenSmartAdd(slide.id)}
                        transition={getSlideTransition(slide.id)}
                        defaultTransition={defaultTransition}
                        onTransitionUpdate={(t) => onTransitionUpdate(slide.id, t)}
                        onTransitionClear={() => onTransitionClear(slide.id)}
                        fromThumbnail={slide.thumbnail}
                        toThumbnail={next.thumbnail}
                        isSaving={savingTransitions[slide.id]}
                        showTransition={hasManifest}
                      />
                    </div>
                  )}
                </SortableScene>
              );
            })}
          </div>
        </SortableContext>
        <DragOverlay>
          {dragged ? (
            <div className="flex h-[60px] w-36 items-end overflow-hidden rounded-[var(--b-radius-control)] bg-b-elevated p-1.5 shadow-lg">
              <span className="truncate text-[11px] font-medium text-b-t1">{dragged.title}</span>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      <IconTipB label="Add a scene" side="top">
        <button
          type="button"
          aria-label="Add a scene"
          onClick={() => onOpenSmartAdd()}
          className="my-2 grid w-9 flex-none place-items-center rounded-[var(--b-radius-control)] text-b-t3 shadow-[inset_0_0_0_1px_hsl(var(--b-line-strong))] transition-colors hover:bg-b-raised hover:text-b-t1"
        >
          <Plus className="h-4 w-4" />
        </button>
      </IconTipB>
      {deletedSlides.length > 0 && (
        <div className="relative my-2 flex flex-none">
          <button
            type="button"
            aria-label={`${deletedSlides.length} deleted scene${deletedSlides.length === 1 ? '' : 's'}`}
            aria-expanded={deletedOpen}
            title="Deleted scenes, to restore"
            onClick={() => setDeletedOpen((o) => !o)}
            className="flex items-center whitespace-nowrap rounded-[var(--b-radius-control)] px-2 text-[12px] font-medium text-b-t3 transition-colors hover:bg-b-raised hover:text-b-t1"
          >
            {deletedSlides.length} deleted
          </button>
          {deletedOpen && (
            <ul className="absolute bottom-full right-0 z-30 mb-2 w-60 rounded-[var(--b-radius-card)] bg-b-elevated p-1 shadow-lg ring-1 ring-[hsl(var(--b-line-strong))]">
              {deletedSlides.map((d) => (
                <li key={d.id}>
                  <button
                    type="button"
                    aria-label={`Restore ${d.title}`}
                    onClick={() => {
                      setDeletedOpen(false);
                      onRestoreDeleted(d.id);
                    }}
                    className="flex w-full items-center gap-2 rounded-[var(--b-radius-chip)] px-2 py-1.5 text-left text-[13px] text-b-t1 transition-colors hover:bg-b-raised"
                  >
                    {d.generatedThumbnail ? (
                      <img src={d.generatedThumbnail} alt="" className="h-6 w-10 flex-none rounded object-cover grayscale" />
                    ) : (
                      <span className="h-6 w-10 flex-none rounded bg-b-raised" />
                    )}
                    <span className="min-w-0 truncate">{d.title}</span>
                    <span className="ml-auto flex-none text-[11px] text-b-t3">Restore</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
